import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { config } from "../../../config";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_AUDIO_TYPES = [
  "audio/mpeg",
  "audio/mp3",
  "audio/wav",
  "audio/x-wav",
  "audio/ogg",
  "audio/x-m4a",
  "audio/mp4",
];
const ALLOWED_AUDIO_EXTENSIONS = /\.(mp3|wav|ogg|m4a)$/i;

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(2)} MB`
    : `${Math.ceil(bytes / 1024)} KB`;

const resolveAudioUrl = (src) => {
  if (!src) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(src)) return src;
  return `${config.file_upload_url}${src}`;
};

const validateAudioFile = (file) => {
  if (!file) return "Please select an audio file.";
  if (file.size > MAX_FILE_SIZE) {
    return "File size exceeds 5MB limit. Please choose a smaller file.";
  }
  const validType = ALLOWED_AUDIO_TYPES.includes(file.type);
  const validExt = ALLOWED_AUDIO_EXTENSIONS.test(file.name);
  if (!validType && !validExt) {
    return "Invalid format. Please upload MP3, WAV, OGG, or M4A.";
  }
  return null;
};

export default function AudioUploadPlayer({ file, savedAudio, onFileChange }) {
  const audioRef = useRef(null);
  const fileInputRef = useRef(null);

  const [objectUrl, setObjectUrl] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLooping, setIsLooping] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (!file) {
      setObjectUrl("");
      return;
    }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const rawSrc = objectUrl || (file ? "" : savedAudio?.path || "");
  const audioSrc = useMemo(() => resolveAudioUrl(rawSrc), [rawSrc]);

  const displayName =
    file?.name || savedAudio?.originalName || "No audio selected";

  const statusLabel = file
    ? `New file • ${formatSize(file.size)} (not saved yet)`
    : savedAudio?.path
      ? "Saved audio"
      : "Please upload an audio file";

  const savedDuration = Number(savedAudio?.duration) || 0;
  const shownDuration = duration || (file ? 0 : savedDuration);

  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);

    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      if (audioSrc) audio.load();
    }
  }, [audioSrc]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.loop = isLooping;
    audio.muted = isMuted;
  }, [isLooping, isMuted]);

  const togglePlay = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !audioSrc) {
      toast.error("No audio file available.");
      return;
    }
    if (!audio.paused) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
    } catch (error) {
      console.error("Audio playback error:", error);
      setIsPlaying(false);
      toast.error("Unable to play audio. Please check the audio file.");
    }
  }, [audioSrc]);

  const handleRestart = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !audioSrc) return;
    audio.currentTime = 0;
    setCurrentTime(0);
    try {
      await audio.play();
    } catch {
      setIsPlaying(false);
    }
  }, [audioSrc]);

  const handleSeek = useCallback((e) => {
    const time = Number(e.target.value);
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(time)) return;
    audio.currentTime = time;
    setCurrentTime(time);
  }, []);

  const toggleMute = useCallback(() => setIsMuted((m) => !m), []);

  const toggleLoop = useCallback(() => {
    const next = !isLooping;
    setIsLooping(next);
    toast.info(next ? "Loop enabled" : "Loop disabled", {
      toastId: "audio-loop",
    });
  }, [isLooping]);

  const handleTimeUpdate = useCallback((e) => {
    setCurrentTime(e.currentTarget.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback((e) => {
    const d = e.currentTarget.duration;
    if (Number.isFinite(d)) setDuration(d);
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const handleAudioError = useCallback(() => {
    setIsPlaying(false);
    if (audioSrc) {
      toast.error("Unable to load this audio file.", {
        toastId: "audio-load-error",
      });
    }
  }, [audioSrc]);

  const handleFileUpload = useCallback(
    (event) => {
      const picked = event.target.files?.[0];
      if (!picked) return;

      const error = validateAudioFile(picked);
      if (error) {
        toast.error(error);
      } else if (typeof onFileChange !== "function") {
        console.error("AudioUploadPlayer: onFileChange prop is missing");
        toast.error("Unable to select file.");
      } else {
        onFileChange(picked);
        toast.success("Audio selected. Click Save to upload.");
      }

      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [onFileChange],
  );

  const handleRemoveSelected = useCallback(() => {
    onFileChange?.(null);
    toast.info("Selected audio removed");
  }, [onFileChange]);

  const hasAudio = Boolean(audioSrc);

  return (
    <div className="d-flex align-items-center justify-content-center p-3">
      <div className="card player-card border shadow-sm p-4 text-center">
        <p
          className="fw-medium mb-1 text-truncate px-2 d-flex align-items-center justify-content-center gap-1"
          title={displayName}
        >
          {hasAudio && <i className="fa-solid fa-check text-success"></i>}
          <span>{displayName}</span>
        </p>
        <p className={`small mb-4 ${file ? "text-warning" : "text-muted"}`}>
          {statusLabel}
        </p>

        <audio
          ref={audioRef}
          src={audioSrc || undefined}
          preload="metadata"
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={handleEnded}
          onError={handleAudioError}
        />

        <div className="player-pill bg-light rounded-pill py-2 px-3 d-flex align-items-center gap-3 shadow-sm mb-4">
          <button
            type="button"
            onClick={togglePlay}
            disabled={!hasAudio}
            className="btn btn-dark rounded-circle d-flex align-items-center justify-content-center play-btn"
            title={isPlaying ? "Pause" : "Play"}
          >
            <i
              className={`fa-solid ${isPlaying ? "fa-pause" : "fa-play ms-1"} small`}
            ></i>
          </button>

          <div className="text-muted font-monospace time-display">
            {formatTime(currentTime)} / {formatTime(shownDuration)}
          </div>

          <input
            type="range"
            min={0}
            max={shownDuration || 0}
            step="0.1"
            value={Math.min(currentTime, shownDuration || 0)}
            onChange={handleSeek}
            disabled={!duration}
            className="form-range flex-grow-1 custom-range-slider"
            aria-label="Audio progress"
          />

          <button
            type="button"
            onClick={toggleMute}
            disabled={!hasAudio}
            className="btn btn-link text-secondary p-0 text-decoration-none"
            title={isMuted ? "Unmute" : "Mute"}
          >
            <i
              className={`fa-solid ${
                isMuted ? "fa-volume-xmark text-danger" : "fa-volume-high"
              }`}
            ></i>
          </button>

          <div className="dropdown">
            <button
              type="button"
              className="btn btn-link text-secondary p-0 text-decoration-none dropdown-toggle hide-arrow"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Audio options"
            >
              <i className="fa-solid fa-ellipsis-vertical"></i>
            </button>

            <ul className="dropdown-menu dropdown-menu-end shadow border-0 py-1 fs-7">
              <li>
                <button
                  type="button"
                  onClick={toggleLoop}
                  disabled={!hasAudio}
                  className="dropdown-item d-flex align-items-center gap-2 py-2"
                >
                  <i
                    className={`fa-solid fa-rotate-right ${
                      isLooping ? "text-primary" : "text-muted"
                    }`}
                  ></i>
                  {isLooping ? "Looping Enabled" : "Loop Audio"}
                </button>
              </li>

              <li>
                <button
                  type="button"
                  onClick={handleRestart}
                  disabled={!hasAudio}
                  className="dropdown-item d-flex align-items-center gap-2 py-2"
                >
                  <i className="fa-solid fa-backward-step text-muted"></i>
                  Restart Track
                </button>
              </li>

              <li>
                <hr className="dropdown-divider my-1" />
              </li>

              <li>
                <button
                  type="button"
                  onClick={handleRemoveSelected}
                  disabled={!file}
                  className="dropdown-item text-danger d-flex align-items-center gap-2 py-2"
                >
                  <i className="fa-solid fa-trash"></i>
                  Remove Selected File
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mb-2">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="btn btn-primary w-100 py-2 rounded-3 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2 upload-btn"
          >
            <i className="fa-solid fa-cloud-arrow-up fs-5"></i>
            <span>{hasAudio ? "Change Audio" : "Upload Audio"}</span>
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.ogg,.m4a,audio/mpeg,audio/wav,audio/ogg,audio/x-m4a"
            onChange={handleFileUpload}
            className="d-none"
          />
        </div>

        <div className="text-muted format-label">
          MP3, WAV, OGG, M4A &bull; Max 2MB
        </div>
      </div>
    </div>
  );
}
